import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TextField, Autocomplete } from "@mui/material";
import { memo } from "react";
import { NumberField } from "@base-ui/react/number-field";

import { GeoRehandleDTO } from "../../DTOs/Geology/GeoRehandleDTO";
import { GeoRehandleDiggerDTO } from "../../DTOs/Geology/GeoRehandleDiggerDTO";
import { GeoRehandleTruckingTruckDTO } from "../../DTOs/Geology/GeoRehandleTruckingTruckDTO";

interface InsertGeoRehandleRowProps {
  state: GeoRehandleDTO;
  setState: React.Dispatch<React.SetStateAction<GeoRehandleDTO>>;
  onAdd: () => Promise<boolean>;
  diggerOptions: GeoRehandleDiggerDTO[];
  isDiggerOptionsLoading: boolean;
  bucketTypeOptions: GeoRehandleTruckingTruckDTO[];
  bucketTypeOptionsLoading: boolean;
  rehandleMovementFromLocationOptions: { value: number, label: string, isDumpOrCrusher: boolean }[];
  isRehandleMovementFromLocationOptionsLoading: boolean;
  rehandleMovementToLocationOptions: { value: number, label: string, isDumpOrCrusher: boolean }[];
  isRehandleMovementToLocationOptionsLoading: boolean;
  isShiftLocked: boolean
}

export const InsertGeoRehandleRow = memo(function InsertRow({
  state,
  setState,
  onAdd,
  diggerOptions,
  isDiggerOptionsLoading,
  bucketTypeOptions,
  bucketTypeOptionsLoading,
  rehandleMovementFromLocationOptions,
  isRehandleMovementFromLocationOptionsLoading,
  rehandleMovementToLocationOptions,
  isRehandleMovementToLocationOptionsLoading,
  isShiftLocked
}: InsertGeoRehandleRowProps) {
  return (
    <ul className="py-1 flex gap-2 border-b border-theme-table-border">
      <li key="digger" className="basis-[20%]">
        <Autocomplete
          classes={{ root: 'w-full' }}
          size="small"
          loading={isDiggerOptionsLoading}
          disabled={isDiggerOptionsLoading || isShiftLocked}
          options={diggerOptions}
          getOptionLabel={(option) => option.plantRef}
          value={diggerOptions.find(x => x.operationId === state.operationId) ?? null}
          isOptionEqualToValue={(option, value) => option.operationId === value.operationId}
          onChange={async (_event, newValue) => {
            if (newValue) {
              setState((prev) => ({ ...prev, operationId: newValue.operationId }));
            }
          }}
          renderInput={(params) => <TextField
            {...params}
            label="Digger"
            slotProps={{
              ...params.slotProps,
              input: {
                ...params.slotProps.input,
                endAdornment: (
                  <>
                    {isDiggerOptionsLoading && <FontAwesomeIcon icon={faSpinner} spin />}
                    {params.slotProps.input.endAdornment}
                  </>
                )
              }
            }}
          />}
        />
      </li>
      <li key="plant-model" className="basis-[20%]">
        <Autocomplete
          classes={{ root: 'w-full' }}
          size="small"
          loading={bucketTypeOptionsLoading}
          disabled={bucketTypeOptionsLoading || isShiftLocked}
          options={bucketTypeOptions}
          getOptionLabel={(option) => option.bucketType}
          value={bucketTypeOptions.find(x => x.bucketTypeId === state.bucketTypeId) ?? null}
          isOptionEqualToValue={(option, value) => option.bucketTypeId === value.bucketTypeId}
          onChange={async (_event, newValue) => {
            if (newValue) {
              setState((prev) => ({ ...prev, bucketTypeId: newValue.bucketTypeId }));
            }
          }}
          renderInput={(params) => <TextField
            {...params}
            label="Plant Model"
            slotProps={{
              ...params.slotProps,
              input: {
                ...params.slotProps.input,
                endAdornment: (
                  <>
                    {bucketTypeOptionsLoading && <FontAwesomeIcon icon={faSpinner} spin />}
                    {params.slotProps.input.endAdornment}
                  </>
                )
              }
            }}
          />}
        />
      </li>
      <li key="movement-from" className="basis-[20%]">
        <Autocomplete
          classes={{ root: 'w-full' }}
          size="small"
          loading={isRehandleMovementFromLocationOptionsLoading}
          disabled={isRehandleMovementFromLocationOptionsLoading || isShiftLocked}
          options={rehandleMovementFromLocationOptions}
          value={rehandleMovementFromLocationOptions.find(x => x.value === state.movementFromId) ?? null}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          onChange={async (_event, newValue) => {
            if (newValue) {
              setState((prev) => ({ ...prev, movementFromId: newValue.value }));
            }
          }}
          renderOption={(props, option) => {
            const { key, ...rest } = props;
            return (
              <li key={key} {...rest}>
                <span className={`${option.isDumpOrCrusher === true ? "text-theme-danger" : ""}`}>{option.label}</span>
              </li>
            )
          }}
          renderInput={(params) => <TextField
            {...params}
            label="Movement From"
            slotProps={{
              ...params.slotProps,
              input: {
                ...params.slotProps.input,
                className: `${rehandleMovementToLocationOptions.find(x => x.value === state.movementToId)?.isDumpOrCrusher === true ? "text-theme-danger" : ""}`,
                style: {
                  transition: "color 0.2s ease-in-out"
                },
                endAdornment: (
                  <>
                    {(isRehandleMovementFromLocationOptionsLoading) && <FontAwesomeIcon icon={faSpinner} spin />}
                    {params.slotProps.input.endAdornment}
                  </>
                )
              }
            }}
          />}
        />
      </li>
      <li key="loads" className="basis-[10%] text-[0.9rem]">
        <NumberField.Root
          disabled={isShiftLocked}
          value={state.loads ?? null}
          onValueChange={(val) => {
            if (val === null || val === state.loads) return;
            setState(prev => ({ ...prev, loads: val ?? 0 }));
          }}
          className="h-full bg-theme-surface-2"
        >
          <NumberField.Group className="relative flex gap-0.5 h-full border border-theme-border rounded-sm">
            <NumberField.Decrement />
            <NumberField.Input
              placeholder="Loads"
              className={`h-full w-full px-1 py-2 bg-theme-surface-2 text-theme-text disabled:text-theme-text-muted transition`}
            />
            <NumberField.Increment />
          </NumberField.Group>
        </NumberField.Root>
      </li>
      <li key="movement-to" className="basis-[20%]">
        <Autocomplete
          classes={{ root: 'w-full' }}
          size="small"
          loading={isRehandleMovementToLocationOptionsLoading}
          disabled={isRehandleMovementToLocationOptionsLoading || isShiftLocked}
          options={rehandleMovementToLocationOptions}
          value={rehandleMovementToLocationOptions.find(x => x.value === state.movementToId) ?? null}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          onChange={async (_event, newValue) => {
            if (newValue) {
              setState((prev) => ({ ...prev, movementToId: newValue.value }));
            }
          }}
          renderOption={(props, option) => {
            const { key, ...rest } = props;
            return (
              <li key={key} {...rest}>
                <span className={`${option.isDumpOrCrusher === true ? "text-theme-danger" : ""}`}>{option.label}</span>
              </li>
            )
          }}
          renderInput={(params) => {
            const selected = rehandleMovementToLocationOptions.find(x => x.value === state.movementToId);

            return <TextField
              {...params}
              label="Movement To"
              slotProps={{
                ...params.slotProps,
                input: {
                  ...params.slotProps.input,
                  className: `${selected?.isDumpOrCrusher === true ? "text-theme-danger" : ""}`,
                  style: {
                    transition: "color 0.2s ease-in-out"
                  },
                  endAdornment: (
                    <>
                      {(isRehandleMovementToLocationOptionsLoading) && <FontAwesomeIcon icon={faSpinner} spin />}
                      {params.slotProps.input.endAdornment}
                    </>
                  )
                }
              }}
            />
          }}
        />
      </li>

      <li className="basis-[10%]">
        <button
          disabled={isShiftLocked}
          type="button"
          onClick={onAdd}
          className="px-4 py-2 rounded bg-theme-info hover:bg-theme-info-hover disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed transition text-white rounded-sm border-none text-sm font-bold"
        >
          Add
        </button>
      </li>
    </ul>
  );
});